// Pure, browser-local helpers for the time & clock tools.
// No external dependencies. Uses the built-in Intl APIs for time zones.

export type TimeZoneOption = {
  id: string; // IANA time zone identifier, e.g. "Europe/London"
  label: string; // Friendly city / region label
};

// A curated list of common IANA time zones covering major world cities.
export const COMMON_TIME_ZONES: readonly TimeZoneOption[] = [
  { id: 'UTC', label: 'UTC' },
  { id: 'Pacific/Honolulu', label: 'Honolulu' },
  { id: 'America/Anchorage', label: 'Anchorage' },
  { id: 'America/Los_Angeles', label: 'Los Angeles' },
  { id: 'America/Denver', label: 'Denver' },
  { id: 'America/Chicago', label: 'Chicago' },
  { id: 'America/Mexico_City', label: 'Mexico City' },
  { id: 'America/New_York', label: 'New York' },
  { id: 'America/Toronto', label: 'Toronto' },
  { id: 'America/Bogota', label: 'Bogota' },
  { id: 'America/Sao_Paulo', label: 'Sao Paulo' },
  { id: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires' },
  { id: 'Atlantic/Reykjavik', label: 'Reykjavik' },
  { id: 'Europe/London', label: 'London' },
  { id: 'Europe/Lisbon', label: 'Lisbon' },
  { id: 'Europe/Madrid', label: 'Madrid' },
  { id: 'Europe/Paris', label: 'Paris' },
  { id: 'Europe/Berlin', label: 'Berlin' },
  { id: 'Europe/Rome', label: 'Rome' },
  { id: 'Europe/Amsterdam', label: 'Amsterdam' },
  { id: 'Europe/Warsaw', label: 'Warsaw' },
  { id: 'Europe/Athens', label: 'Athens' },
  { id: 'Europe/Istanbul', label: 'Istanbul' },
  { id: 'Europe/Moscow', label: 'Moscow' },
  { id: 'Africa/Cairo', label: 'Cairo' },
  { id: 'Africa/Lagos', label: 'Lagos' },
  { id: 'Africa/Johannesburg', label: 'Johannesburg' },
  { id: 'Africa/Nairobi', label: 'Nairobi' },
  { id: 'Asia/Jerusalem', label: 'Jerusalem' },
  { id: 'Asia/Dubai', label: 'Dubai' },
  { id: 'Asia/Tehran', label: 'Tehran' },
  { id: 'Asia/Karachi', label: 'Karachi' },
  { id: 'Asia/Kolkata', label: 'Kolkata' },
  { id: 'Asia/Dhaka', label: 'Dhaka' },
  { id: 'Asia/Bangkok', label: 'Bangkok' },
  { id: 'Asia/Jakarta', label: 'Jakarta' },
  { id: 'Asia/Shanghai', label: 'Shanghai' },
  { id: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { id: 'Asia/Singapore', label: 'Singapore' },
  { id: 'Asia/Tokyo', label: 'Tokyo' },
  { id: 'Asia/Seoul', label: 'Seoul' },
  { id: 'Australia/Perth', label: 'Perth' },
  { id: 'Australia/Sydney', label: 'Sydney' },
  { id: 'Pacific/Auckland', label: 'Auckland' },
];

export function zoneLabel(id: string): string {
  const found = COMMON_TIME_ZONES.find((zone) => zone.id === id);
  return found ? found.label : id.replace(/_/g, ' ');
}

// Returns the viewer's local IANA time zone, falling back to UTC.
export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// Formats the time portion for a given date + time zone.
export function formatZoneTime(
  date: Date,
  timeZone: string,
  options: { hour12?: boolean; seconds?: boolean } = {},
): string {
  const { hour12 = false, seconds = true } = options;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      ...(seconds ? { second: '2-digit' } : {}),
      hour12,
    }).format(date);
  } catch {
    return '--:--';
  }
}

// Formats the date portion (weekday, day, month, year) for a time zone.
export function formatZoneDate(date: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  } catch {
    return '';
  }
}

// Computes the UTC offset (in minutes) for a given instant in a time zone.
export function zoneOffsetMinutes(date: Date, timeZone: string): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(date);
    const map: Record<string, number> = {};
    for (const part of parts) {
      if (part.type !== 'literal') map[part.type] = Number(part.value);
    }
    let hour = map.hour;
    if (hour === 24) hour = 0;
    const asUtc = Date.UTC(map.year, map.month - 1, map.day, hour, map.minute, map.second);
    return Math.round((asUtc - date.getTime()) / 60000);
  } catch {
    return 0;
  }
}

// Renders a UTC offset in minutes as a label like "UTC+05:30" or "UTC-08:00".
export function formatOffsetLabel(offsetMinutes: number): string {
  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `UTC${sign}${pad(hours)}:${pad(minutes)}`;
}

// --- Wall-clock <-> instant conversion for the time-zone converter ---

// Interprets the given wall-clock fields as a local time in `timeZone` and
// returns the corresponding UTC instant (a Date).
export function wallTimeToInstant(
  fields: {
    year: number;
    month: number; // 1-12
    day: number;
    hour: number;
    minute: number;
  },
  timeZone: string,
): Date {
  const { year, month, day, hour, minute } = fields;
  // First guess: treat the fields as if they were UTC.
  const guess = Date.UTC(year, month - 1, day, hour, minute, 0);
  // How far off is the guessed instant's wall time from what we want?
  const offset = zoneOffsetMinutes(new Date(guess), timeZone);
  return new Date(guess - offset * 60000);
}

// Parses an <input type="datetime-local"> value ("YYYY-MM-DDTHH:mm").
export function parseDateTimeLocal(value: string): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
} | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };
}

// Builds a datetime-local string for the given instant in the viewer's zone.
export function toDateTimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

// --- Duration math for the time calculator ---

export type Duration = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export const ZERO_DURATION: Duration = { days: 0, hours: 0, minutes: 0, seconds: 0 };

export function durationToMs(duration: Duration): number {
  return (
    duration.days * 86400000 +
    duration.hours * 3600000 +
    duration.minutes * 60000 +
    duration.seconds * 1000
  );
}

// Breaks a millisecond span into a normalized day/hour/minute/second duration.
export function msToDuration(ms: number): Duration {
  let total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  total -= days * 86400;
  const hours = Math.floor(total / 3600);
  total -= hours * 3600;
  const minutes = Math.floor(total / 60);
  const seconds = total - minutes * 60;
  return { days, hours, minutes, seconds };
}

export function formatDuration(duration: Duration): string {
  const parts: string[] = [];
  if (duration.days) parts.push(`${duration.days} day${duration.days === 1 ? '' : 's'}`);
  if (duration.hours) parts.push(`${duration.hours} hour${duration.hours === 1 ? '' : 's'}`);
  if (duration.minutes)
    parts.push(`${duration.minutes} minute${duration.minutes === 1 ? '' : 's'}`);
  if (duration.seconds || parts.length === 0)
    parts.push(`${duration.seconds} second${duration.seconds === 1 ? '' : 's'}`);
  return parts.join(', ');
}

// Formats a full local date + time for display of a calculator result.
export function formatFullDateTime(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}
